import { t } from "@lingui/core/macro"
import { Trans } from "@lingui/react/macro"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import { createServerFn } from "@tanstack/react-start"
import { History } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"
import { z } from "zod"
import { PermissionGate } from "@/components/rbac/PermissionGate"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import {
  deleteStoreConfigurationVersion,
  getStoreConfigurationVersion,
  saveStoreConfigurationVersion,
  updateStoreConfigurationVersion,
} from "@/features/project-editor/server/projectServer"
import {
  StoreDataForm,
  type StoreConfigurationFormValues,
} from "@/features/store-configuration/components/StoreDataForm"
import { buildStoreMetadataFromConfig } from "@/features/store-configuration/core/utils/storeMetadata"
import { storeConfigurationInputSchema } from "@/features/stores/storeConfigurationSchemas"
import { usePermissions } from "@/hooks/usePermissions"
import { authFunctionMiddleware } from "@/lib/auth-middleware"

const Params = z.object({
  storeId: z.string(),
  configurationId: z.string(),
})

const getConfigForPage = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .inputValidator(Params)
  .handler(async ({ data }) => {
    return await getStoreConfigurationVersion({ data })
  })

export const Route = createFileRoute(
  "/(authenticated)/settings/stores/$storeId/configurations/$configurationId",
)({
  params: Params,
  loader: ({ context, params }) => {
    void context.queryClient.ensureQueryData(queryKey(params))
  },
  component: EditConfigurationPage,
})

const queryKey = (params: z.infer<typeof Params>) => ({
  queryKey: ["store-config", params.storeId, params.configurationId],
  queryFn: () => getConfigForPage({ data: params }),
})

function EditConfigurationPage() {
  const params = Route.useParams()
  const navigate = useNavigate()
  const { data } = useQuery(queryKey(params))
  const queryClient = useQueryClient()
  const [saveAsNewVersion, setSaveAsNewVersion] = useState(false)
  const { hasPermission } = usePermissions()
  const canUpdate = hasPermission("storeVersion", "update").allowed

  const initialMetadata = data
    ? buildStoreMetadataFromConfig(data.configuration, data.metadataExtension ?? undefined)
    : undefined
  const defaultValidFrom = data?.configuration?.validFrom
    ? new Date(data.configuration.validFrom).toISOString().slice(0, 10)
    : undefined
  const defaultValidTo = data?.configuration?.validTo
    ? new Date(data.configuration.validTo).toISOString().slice(0, 10)
    : ""

  const saveMutation = useMutation({
    mutationFn: async (formData: StoreConfigurationFormValues) => {
      const parsed = storeConfigurationInputSchema.parse({
        ...formData,
        storeId: params.storeId,
      })
      if (saveAsNewVersion) {
        await saveStoreConfigurationVersion({ data: parsed })
      } else {
        await updateStoreConfigurationVersion({
          data: { ...parsed, configurationId: params.configurationId },
        })
      }
    },
    onSuccess: () => {
      toast.success(t`Configuration saved`)
      void queryClient.invalidateQueries({
        queryKey: ["store-detail", params.storeId],
      })
      void navigate({
        to: "/settings/stores/$storeId",
        params: { storeId: params.storeId },
      })
    },
    onError: () => toast.error(t`Failed to save configuration`),
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await deleteStoreConfigurationVersion({
        data: {
          storeId: params.storeId,
          configurationId: params.configurationId,
        },
      })
    },
    onSuccess: () => {
      toast.success(t`Configuration archived`)
      void queryClient.invalidateQueries({
        queryKey: ["store-detail", params.storeId],
      })
      void navigate({
        to: "/settings/stores/$storeId",
        params: { storeId: params.storeId },
      })
    },
    onError: () => toast.error(t`Failed to delete configuration`),
  })

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="mb-2 text-xs uppercase tracking-wider text-neutral-500 dark:text-neutral-300">
            <Trans>Store configuration</Trans>
          </div>
          <h1 className="font-serif text-4xl font-bold text-neutral-900 dark:text-white">
            {canUpdate ? <Trans>Edit configuration</Trans> : <Trans>View configuration</Trans>}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <PermissionGate resource="auditLog" action="read">
            <Button variant="outline" asChild className="gap-2 text-sm">
              <Link
                to="/settings/audit"
                search={{
                  entityType: "storeConfiguration",
                  entityId: params.configurationId,
                }}
                className="inline-flex items-center"
              >
                <History className="h-4 w-4" />
                <Trans>Audit log</Trans>
              </Link>
            </Button>
          </PermissionGate>
          <PermissionGate resource="storeVersion" action="delete">
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? t`Archiving...` : t`Archive configuration`}
            </Button>
          </PermissionGate>
          <Button variant="outline" asChild className="text-sm">
            <Link to="/settings/stores/$storeId" params={{ storeId: params.storeId }}>
              <Trans>Back to store</Trans>
            </Link>
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {canUpdate ? (
              <Trans>Edit store configuration</Trans>
            ) : (
              <Trans>Store configuration details</Trans>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data?.configuration && (
            <div className="text-muted-foreground mb-4 grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-foreground font-medium">
                  <Trans>Valid from</Trans>:
                </span>{" "}
                {new Intl.DateTimeFormat("en", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                }).format(new Date(data.configuration.validFrom))}
              </div>
              <div>
                <span className="text-foreground font-medium">
                  <Trans>Valid to</Trans>:
                </span>{" "}
                {data.configuration.validTo
                  ? new Intl.DateTimeFormat("en", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    }).format(new Date(data.configuration.validTo))
                  : t`Current`}
              </div>
            </div>
          )}
          {canUpdate && (
            <div className="mb-4 flex items-center gap-2">
              <Checkbox
                id="save-new-version"
                checked={saveAsNewVersion}
                onCheckedChange={(val) => setSaveAsNewVersion(Boolean(val))}
              />
              <Label htmlFor="save-new-version" className="text-sm font-normal">
                <Trans>Save as new version (keep history)</Trans>
              </Label>
            </div>
          )}
          <StoreDataForm
            initialData={initialMetadata}
            onSave={canUpdate ? (values) => saveMutation.mutate(values) : undefined}
            isLoading={saveMutation.isPending}
            defaultValidFrom={defaultValidFrom}
            defaultValidTo={defaultValidTo}
            readOnly={!canUpdate}
          />
        </CardContent>
      </Card>
    </div>
  )
}
